import type { Static } from "@sinclair/typebox";
import {
  type AdminDownload,
  type BotGuild,
  DOWNLOAD_STATUSES,
  type DownloadStatus,
  type RouteById,
  type SuccessOf,
  SYNC_STREAMS,
  type SyncRun,
  type SyncStreamName,
} from "@volgo/core/contract";
import { type ReactNode, useState } from "react";
import { useGalleryInfo } from "../../downloads/useGalleryInfo";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { formatCount, formatDateTime } from "../../ui/format";
import { GalleryLink } from "../../ui/GalleryLink";
import { Icon } from "../../ui/icons";
import {
  DOWNLOAD_STATUS_LABELS,
  ORIGIN_LABELS,
  PRIORITY_LABELS,
  ROUTE_LABELS,
  STREAM_LABELS,
  VERDICT_LABELS,
} from "../../ui/labels";
import { PagedFooter } from "../../ui/PagedFooter";
import { Segmented } from "../../ui/Segmented";
import { usePaged } from "../../ui/useLoad";
import { BlockedMark, JobProgress } from "../DownloadsPage";

/** The pieces of the ops tab (Spec M-1): what used to be the sync and all-downloads screens, now in drawers. */
export type Run = Static<typeof SyncRun>;
export type Overview = SuccessOf<RouteById<"admin.syncStreams">>;
export type SyncStatus = SuccessOf<RouteById<"admin.syncStatus">>;
export type Stream = Overview["items"][number];
export type Job = Static<typeof AdminDownload>;
export type Guild = Static<typeof BotGuild>;
export type Rule = SuccessOf<RouteById<"admin.listRules">>["items"][number];
type RunFilter = "all" | SyncStreamName;
type StatusFilter = "all" | DownloadStatus;
type OriginFilter = "all" | Job["origin"];
/** what the all-downloads drawer opens with; a flow node or an entry point picks it */
export interface DownloadFilter {
  status?: DownloadStatus;
  origin?: Job["origin"];
  ruleId?: string;
}

const REASONS: Record<NonNullable<Run["finishReason"]>, string> = {
  caught_up: "최신 목록까지 확인",
  end_of_list: "목록 끝까지 확인",
  request_limit: "요청 상한 · 다음 실행에서 계속",
  time_budget: "시간 상한 · 다음 실행에서 계속",
  queue_empty: "지금 처리할 대상 없음",
  item_failures: "일부 항목 보류 · 수집은 다음 실행에서 계속",
};
export const BANDS: Record<number, string> = { 3: "최근 1일", 6: "1~7일", 24: "7~20일", 72: "20~60일" };
export const STREAM_ROLES: Record<SyncStreamName, string> = {
  "ex-head": "E-Hentai 첫 페이지부터 새 작품을 찾습니다",
  meta: "제목·태그·표지를 받아 오고 60일 안 작품을 다시 확인합니다",
  "ex-expunged": "목록에서 빠진(expunged) 작품을 확인합니다",
};
const RUN_FILTERS: { value: RunFilter; label: string }[] = [
  { value: "all", label: "전체" },
  ...SYNC_STREAMS.map((value) => ({ value, label: STREAM_LABELS[value] })),
];
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "전체" },
  ...DOWNLOAD_STATUSES.map((value) => ({ value, label: DOWNLOAD_STATUS_LABELS[value] })),
];
const ORIGIN_FILTERS: { value: OriginFilter; label: string }[] = [
  { value: "all", label: "전체" },
  ...(Object.keys(ORIGIN_LABELS) as Job["origin"][]).map((value) => ({ value, label: ORIGIN_LABELS[value] })),
];

export function resultLabel(run: Run, active = false) {
  if (!run.finishedAt) return active ? "수집 중" : "종료 확인 전 · 기록 새로고침 필요";
  if (run.verdict && run.verdict !== "ok") return `보호 중단으로 종료 · ${VERDICT_LABELS[run.verdict]}`;
  if (run.result === "banned") return "보호 중단으로 종료 · 접속 차단";
  if (run.result === "error")
    return run.verdict === "ok" ? "실행 실패 · 자동 재시도 대상" : "오류 · 종료 방식 미기록";
  if (run.failed) return "일부 항목 보류 · 수집은 다음 실행에서 계속";
  if (run.finishReason) return REASONS[run.finishReason];
  return run.result === "ok" ? "정상 종료" : "일부 처리 · 종료 사유 미기록";
}

export function processed(run: Run) {
  if (run.stream !== "meta") return `신규 ${formatCount(run.newGids)} · 기존 ${formatCount(run.knownGids)}`;
  if (run.initialFetched === null) return "처리 건수 미집계";
  return `최초 채움 ${formatCount(run.initialFetched)} · 재조회 ${formatCount(run.refreshed ?? 0)} · 실패 ${formatCount(run.failed ?? 0)}`;
}

/** A lease in the future: a run holds the stream right now. */
export function isActive(stream: Stream, checkedAt: string) {
  return stream.leaseExpiresAt !== null && Date.parse(stream.leaseExpiresAt) > Date.parse(checkedAt);
}

export function streamState(stream: Stream, checkedAt: string) {
  if (stream.pausedAt)
    return {
      tone: "bad",
      text: `일시정지${stream.pausedReason ? ` · ${VERDICT_LABELS[stream.pausedReason]}` : ""}`,
    } as const;
  if (isActive(stream, checkedAt)) return { tone: "run", text: "수집 중" } as const;
  if (stream.leaseExpiresAt) return { tone: "idle", text: "이전 실행 시간 초과" } as const;
  if (stream.latestRun?.result === "error" && stream.latestRun.verdict === "ok")
    return { tone: "idle", text: "실행 실패 · 다음 예약 실행 대기" } as const;
  return { tone: "idle", text: "실행 대기" } as const;
}

export function dueOf(overview: Overview) {
  return overview.refreshBands.reduce((sum, band) => sum + band.dueCount, overview.extraRefreshDueCount);
}

/** A drawer of the ops tab: the gallery drawer's window (Escape, focus back), with a title and a close button. */
export function OpsDrawer({
  title,
  onClose,
  footer,
  wide = false,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /** the all-downloads table needs the room */
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog
      label={typeof title === "string" ? title : "운영 서랍"}
      className={wide ? "drawer ops-drawer ops-drawer-wide" : "drawer ops-drawer"}
      drawer
      onClose={onClose}
    >
      <div className="dw-bar">
        <h2 className="ops-drawer-title">{title}</h2>
        <button type="button" className="iconbtn gw-close" aria-label="닫기" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="ops-drawer-body">{children}</div>
      {footer && <div className="ops-drawer-foot">{footer}</div>}
    </Dialog>
  );
}

/** One stream: what it does, why it stopped, its latest run, resuming it, and the run history. */
export function StreamDrawer({
  stream,
  overview,
  round,
  resuming,
  onResume,
  onClose,
}: {
  stream: Stream;
  overview: SyncStatus;
  /** a new value (the page's refresh, a resume) loads the history again */
  round: number;
  resuming: boolean;
  onResume: () => void;
  onClose: () => void;
}) {
  const { client } = useSession();
  const [filter, setFilter] = useState<RunFilter>(stream.name);
  const runs = usePaged<Run>(
    (cursor) =>
      client.call("admin.syncRuns", {
        query: { limit: 25, ...(filter === "all" ? {} : { stream: filter }), ...(cursor ? { cursor } : {}) },
      }),
    `${filter}:${round}`,
  );
  const label = STREAM_LABELS[stream.name];
  const state = streamState(stream, overview.checkedAt);
  const active = isActive(stream, overview.checkedAt) && !stream.pausedAt;
  const latestOf = (run: Run) =>
    overview.items.some((s) => s.latestRun?.id === run.id && !s.pausedAt && isActive(s, overview.checkedAt));
  return (
    <OpsDrawer
      title={label}
      onClose={onClose}
      footer={
        <>
          {stream.pausedAt && (
            <Button
              variant="primary"
              aria-label={`${label} 일시정지 해제`}
              disabled={resuming}
              onClick={onResume}
            >
              재개
            </Button>
          )}
          <Button onClick={runs.reload}>실행 기록 새로고침</Button>
        </>
      }
    >
      <article className="ops-stream" aria-label={label} data-tone={state.tone}>
        <p className="note">{STREAM_ROLES[stream.name]}</p>
        <p className={stream.pausedAt ? "note-warn" : ""}>{state.text}</p>
        {stream.latestRun ? (
          <>
            <p className="note">
              최근 실행 {formatDateTime(stream.latestRun.startedAt)}
              <br />
              {resultLabel(stream.latestRun, active)}
            </p>
            <p>{processed(stream.latestRun)}</p>
          </>
        ) : (
          <p className="note">실행 기록 없음</p>
        )}
        {stream.name !== "meta" && (
          <p className="note">
            가장 새 GID {stream.highWaterGid ?? "—"} · 진행 GID {stream.cursorGid ?? "—"}
          </p>
        )}
      </article>
      <p className="note">
        expunged는 일반 목록에서 제외된 상태이며 영구 삭제와 다릅니다. 일시정지 원인을 해결한 뒤 해제하면 다음
        예약 실행부터 재개합니다.
      </p>
      <h3 className="ops-h3">실행 기록</h3>
      <Segmented legend="흐름" options={RUN_FILTERS} isOn={(v) => v === filter} onPick={setFilter} />
      <p className="note">
        펼쳐 본 기록은 자동 갱신하지 않습니다. 이전 실행의 처리 건수·종료 사유는 미집계일 수 있습니다.
      </p>
      <div className="sync-runs">
        {runs.items.map((run) => (
          <details key={run.id}>
            <summary>
              <span>
                {STREAM_LABELS[run.stream]} · {formatDateTime(run.startedAt)}
              </span>
              <span
                className={
                  run.result === "error" || run.result === "banned" || run.failed ? "note-warn" : "note"
                }
              >
                {resultLabel(run, latestOf(run))}
              </span>
              <span>{processed(run)}</span>
            </summary>
            <div className="sync-run-detail">
              <p className="note">
                요청 {formatCount(run.requests)}회 · 소요{" "}
                {run.finishedAt
                  ? `${Math.max(0, (Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000).toFixed(1)}초`
                  : "—"}
              </p>
              <p className="note">
                이번 실행 종료 {run.finishedAt ? formatDateTime(run.finishedAt) : "기록 없음"}
                {run.note ? ` · ${run.note}` : ""}
              </p>
            </div>
          </details>
        ))}
      </div>
      <PagedFooter list={runs} emptyTitle="실행 기록이 없습니다" />
    </OpsDrawer>
  );
}

/** The metadata backlog and the four refresh bands of the last 60 days. */
export function RefreshDrawer({ overview, onClose }: { overview: Overview; onClose: () => void }) {
  return (
    <OpsDrawer title="다시 확인(최근 60일)" onClose={onClose}>
      <div className="sync-grid">
        <div className="sync-card">
          <h3>정보를 아직 못 받은 작품</h3>
          <strong className="sync-number">{formatCount(overview.pendingMetaCount)}개</strong>
          <p className="note">게시일과 무관하게 모두 채웁니다. 재시도 대기도 포함합니다.</p>
        </div>
        <div className="sync-card">
          <h3>다시 확인 기한이 지난 작품</h3>
          <strong className="sync-number">{formatCount(dueOf(overview))}개</strong>
          <p className="note">지금 선택 가능한 대상입니다. 처리 중·재시도 유예는 제외합니다.</p>
        </div>
        <div className="sync-card">
          <h3>세 흐름</h3>
          <strong className="sync-number">
            일시정지 {overview.items.filter((s) => s.pausedAt).length}개
          </strong>
          <p className="note">
            상태 확인 {formatDateTime(overview.checkedAt)}
            <br />
            건수 집계 {formatDateTime(overview.countsAt)}
          </p>
        </div>
      </div>
      <p className="page-lede">
        최초 수집과 다시 확인을 한 작업 안에서 번갈아 처리합니다. 한쪽에 대기가 없으면 다른 쪽을 계속
        처리합니다.
      </p>
      <div className="sync-grid sync-bands">
        {overview.refreshBands.map((band) => (
          <article className="sync-card" key={band.intervalHours}>
            <h3>{BANDS[band.intervalHours]}</h3>
            <p>
              {band.intervalHours < 24 ? `${band.intervalHours}시간` : `${band.intervalHours / 24}일`}마다
              다시 확인
            </p>
            <p className="note">수집된 대상 {formatCount(band.targetCount)}개</p>
            <p>
              기한 도래 <strong>{formatCount(band.dueCount)}개</strong>
            </p>
          </article>
        ))}
      </div>
      <p className="note">
        갤러리 게시일 기준이며 경계 시점은 다음 구간에 포함됩니다. 60일 이상은 정기 재조회 대상에서
        제외합니다.
        <br />
        기간 밖 상태 재확인·재시도 중 기한이 된 대상: {formatCount(overview.extraRefreshDueCount)}개
      </p>
    </OpsDrawer>
  );
}

/** Who asked: the account on the web, the rule, or the Discord server and user of the bot. */
function requester(job: Job, guilds: readonly Guild[]) {
  if (job.origin === "web") return job.requesterName;
  if (job.origin === "rule") return job.ruleName ?? "지운 규칙";
  const guild = guilds.find((g) => g.guildId === job.discordGuildId);
  return `${guild?.note || `서버 ${job.discordGuildId ?? "—"}`} · 사용자 ${job.discordUserId ?? "—"}`;
}

/**
 * Download jobs of every account, bot and rule. `compact` (the board's recent list) leaves out the route,
 * the priority and GP. `guilds` names the Discord servers the bot's requests came from.
 */
export function DownloadTable({
  jobs,
  guilds = [],
  compact = false,
}: {
  jobs: readonly Job[];
  guilds?: readonly Guild[];
  compact?: boolean;
}) {
  const infoOf = useGalleryInfo(jobs);
  if (jobs.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">갤러리</th>
            <th scope="col">출처</th>
            <th scope="col">요청</th>
            {!compact && <th scope="col">받는 방법</th>}
            <th scope="col">상태</th>
            <th scope="col">진행</th>
            {!compact && <th scope="col">우선순위</th>}
            {!compact && <th scope="col">GP</th>}
            <th scope="col">요청한 때</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} data-gid={job.gid} data-policy={infoOf(job).verdict}>
              <th scope="row">
                <GalleryLink gid={job.gid}>{infoOf(job).title ?? `갤러리 ${job.gid}`}</GalleryLink>
                {job.galleryTitle === null && <span className="note">제목 미수집</span>}
                {infoOf(job).verdict === "blocked" && (
                  <>
                    {" "}
                    <BlockedMark />
                  </>
                )}
              </th>
              <td>
                <span className="ops-org" data-origin={job.origin}>
                  {ORIGIN_LABELS[job.origin]}
                </span>
              </td>
              <td>{requester(job, guilds)}</td>
              {!compact && <td>{ROUTE_LABELS[job.route]}</td>}
              <td>{DOWNLOAD_STATUS_LABELS[job.status]}</td>
              <td>
                <JobProgress job={job} />
              </td>
              {!compact && <td>{PRIORITY_LABELS[job.priority]}</td>}
              {!compact && <td>{job.spentGp ?? "—"}</td>}
              <td>{formatDateTime(job.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every download job, by status, origin and rule (a rule is picked among the rule origin). A snapshot: the
 * list is reloaded by its filter.
 */
export function DownloadsDrawer({
  initial = {},
  rules,
  guilds,
  onClose,
}: {
  initial?: DownloadFilter;
  rules: readonly Rule[];
  guilds: readonly Guild[];
  onClose: () => void;
}) {
  const { client } = useSession();
  const [filter, setFilter] = useState<StatusFilter>(initial.status ?? "all");
  const [origin, setOrigin] = useState<OriginFilter>(initial.origin ?? "all");
  const [ruleId, setRuleId] = useState(initial.ruleId ?? "");
  const rule = origin === "rule" && ruleId ? ruleId : undefined;
  const list = usePaged<Job>(
    (cursor) =>
      client.call("admin.listDownloads", {
        query: {
          limit: 50,
          ...(filter === "all" ? {} : { status: filter }),
          ...(origin === "all" ? {} : { origin }),
          ...(rule ? { ruleId: rule } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
    `${filter}:${origin}:${rule ?? ""}`,
  );
  return (
    <OpsDrawer title="전체 다운로드" onClose={onClose} wide>
      <Segmented legend="상태" options={STATUS_FILTERS} isOn={(v) => v === filter} onPick={setFilter} />
      <Segmented legend="출처" options={ORIGIN_FILTERS} isOn={(v) => v === origin} onPick={setOrigin} />
      {origin === "rule" && (
        <label className="field ops-rule-pick">
          규칙
          <select className="select" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
            <option value="">모든 규칙</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <DownloadTable jobs={list.items} guilds={guilds} />
      <PagedFooter list={list} emptyTitle="다운로드가 없습니다" />
    </OpsDrawer>
  );
}
